import { OryxGameController } from "@westpierstudio/core-module-oryx/game/OryxGameController";
import { showActivateFreeRoundFinishedDialog, showGamingLimitsReachedErrorDialog, showGenericCommsErrorDialog, showInsufficientFundsDialog } from "@westpierstudio/core-module-oryx/utils/errorHandling";
import { get, asBoolean } from "@westpierstudio/core-module-oryx/services/rgsdata/URLParsing";
import { EventEmitter } from "@westpierstudio/luma/events";
import { reset, Resettable } from "@westpierstudio/luma/utils/reset";
import { Flow } from "@westpierstudio/luma/utils/flow";
import { SlotGameUIView, UIBetConfig } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/SlotGameUIView";
import { LuckyWealthCatGameClient } from "game/LuckyWealthCatGameClient";
import { GameState, LuckyWealthCatGameModel } from "game/LuckyWealthCatGameModel";
import { LuckyWealthCatGameView } from "game/LuckyWealthCatGameView";
import { GameServices } from "./GameServices";
import { Background } from "components/Background";
import { BasicView } from "components/BasicView";
import { Reels } from "components/reels/Reels";
import { OverlayMultiplierBadges } from "components/multiplierbadges/OverlayMultiplierBadges";
import { ReelTopDisplay } from "components/ReelTopDisplay";
import { WinLines } from "components/winlines/WinLines";
import { WinlineCount } from "components/WinlineCount";
import { MultiplierPanels } from "components/multiplierpanels/MultiplierPanels";
import { JackpotBowl } from "components/jackpotbowl/JackpotBowl";
import { CollectionSymbol } from "components/CollectionSymbol";
import { WheelPrizePanel } from "components/wheelprizepanel/WheelPrizePanel";
import { Wheel } from "components/wheel/Wheel";
import { FreeSpinCounter } from "components/FreeSpinCounter";
import { BonusSummaryScroll } from "components/BonusSummaryScroll";
import { BonusTransitionPanels } from "components/BonusTransitionPanels";
import { BigWin } from "@westpierstudio/core-module-oryx/components/bigwin";
import { SoundPlayer } from "components/SoundPlayer";
import { OpenGameEvent, WedgesConfig } from "servertypes/gameevents/OpenGame";
import { InitialSpinEvent } from "servertypes/gameevents/InitialSpin";
import { InitialWheelEvent, WheelData } from "servertypes/gameevents/Wheel";
import { FreeSpinWheelEvent } from "servertypes/gameevents/FreeSpinWheel";
import { FreeSpinEvent } from "servertypes/gameevents/FreeSpin";
import { SpinErrorEvent } from "servertypes/gameevents/SpinError";
import { GameRequest } from "servertypes/ServerEvents";
import { BetData, CoreSymbolID, CoreSymbolNumberMap, FinalFreeSpinsCounter, getMultiplierPanelIndex, JackpotWedgeID, LastEventData,
    MultiplierCountersMap, Multipliers, NetPosition, ReelSetSymbols, TriggeringState } from "servertypes/SharedTypes";
import { PositionList } from "components/wheelprizepanel/WheelPrizePanelModel";
import { Signal } from "type-signals";
import { PeriodicInspectionEvent } from "@westpierstudio/core-module-oryx/services/comms/types/PeriodicInspection";
import { KSGLogo } from "components/KSGLogo";
import { GameDialogType } from "@westpierstudio/core-module-oryx/components/ui/dialog/DialogTypes";
import { Precog } from "components/Precog";
import { LuckyWealthCatDebugGUI } from "gui/LuckyWealthCatDebugGUI";
import { allSignals } from "@westpierstudio/luma/utils/signal";
import { MessageCycleController, MessageCycleSettings } from "utils/MessageCycleController";
import { MinTimeBetweenWagersAction } from "utils/MinTimeBetweenWagersAction";

class GameSignals {
    skipBigWin = new Signal();
    skipWinRollUp = new Signal();
    skipSpin = new Signal();
    skipWheel = new Signal();
    skipFreeSpinCounter = new Signal<(total: number) => void>();
    skipJackpot = new Signal();
}

export class LuckyWealthCatGameController extends OryxGameController<LuckyWealthCatGameView, LuckyWealthCatGameModel> {
    private resetList: Resettable[] = [];
    private flow!: Flow;
    private background!: Background;
    private reels!: Reels;
    private overlayMultiplierBadges!: OverlayMultiplierBadges;
    private winlines!: WinLines;
    private winlineCount!: WinlineCount;
    private multiplierPanels!: MultiplierPanels;
    private reelTopDisplay!: ReelTopDisplay;
    private freeSpinCounter!: FreeSpinCounter;
    private jackpotBowl!: JackpotBowl;
    private jackpotBowlLayerIndex!: number;
    private collectionSymbol!: CollectionSymbol;
    private wheelPrizePanel!: WheelPrizePanel;
    private wheel!: Wheel;
    private bigWin!: BigWin;
    private bonusSummaryScroll!: BonusSummaryScroll;
    private bonusTransitionPanels!: BonusTransitionPanels;
    private precog!: Precog;
    private ksgLogo!: KSGLogo;
    private soundplayer!: SoundPlayer;
    signals = new GameSignals();
    private messageCycleController!: MessageCycleController;
    debugGUI?: LuckyWealthCatDebugGUI;

    constructor(protected override readonly services: GameServices, view: LuckyWealthCatGameView, model: LuckyWealthCatGameModel,
        gameEmitter: EventEmitter, protected override client: LuckyWealthCatGameClient) {
        super(services, view, model, gameEmitter, client);
    }

    override setup() {
        super.setup();

        this.resetList.push(this.flow = new Flow(this.gameEmitter));
        this.resetList.push(this.soundplayer = new SoundPlayer(this.services));

        this.setupComponents();
        this.resize();
        this.setupSkip();
        this.setupClient();
        this.setupGameSpecificDebugGUI();

        this.services.application.onTick.add(this.tick, this);
        this.services.layout.onResize.add(this.resize, this);
    }

    override reset() {
        super.reset();
        reset(this.resetList);

        if (this.jackpotBowl) {
            this.view.rootGameContent.addChildAt(this.jackpotBowl, this.jackpotBowlLayerIndex);
        }

        this.client.establishConnection();
    }

    override initiateComms() {
        const showDebugUI = process.env.NODE_ENV === `production` ? false : asBoolean(get(`debugGUI`), true);
        if (showDebugUI) {
            this.setupDebugGUI();
        }

        this.client.establishConnection();
    }

    private setupClient(): void {
        this.client.signals.openGame.add(this.openGame, this);
        this.client.signals.initialSpin.add(this.initialSpin, this);
        this.client.signals.initialWheel.add(this.initialWheel, this);
        this.client.signals.freeSpin.add(this.freeSpin, this);
        this.client.signals.freeSpinWheel.add(this.freeSpinWheel, this);
        this.client.signals.spinError.add(this.spinError, this);
        this.client.signals.periodicInspection.add(this.periodicInspection, this);

        const openGameResponse = this.client.getLatestOpenGameResponse<OpenGameEvent>();
        if (openGameResponse) {
            this.openGame(openGameResponse);
        }
    }

    private setupDebugGUI(): void {
        const forcerGroups = this.services.resources.getSetting(`dev`).forcerGroups;

        const debugUI = this.debugGUI = new LuckyWealthCatDebugGUI(this.services, this.model);

        // delayed setup
        this.client.signals.openGame.once(event => debugUI.setupVersions(event.body.data.artifactVersions));
        this.client.signals.openGame.once(event => debugUI.setupRoundHistory(event.body.lastRoundId));
        this.client.adminSignals.forcerScenariosLoaded.once(scenarios => debugUI.setupForcer(scenarios, forcerGroups));

        // devui signals
        debugUI.signals.resetGame.add(() => this.resetGame.dispatch());
        debugUI.signals.force.add((value) => this.client.sendAdminForceRequest(value));
        debugUI.signals.setCurrencyCode.add(currencyCode => this.client.sendAdminSetCurrencyCodeRequest(currencyCode));
        this.client.signals.initialSpin.add(event => debugUI.setRoundID(event.body.data.roundId));
    }

    private setupGameSpecificDebugGUI() {
        if (!this.debugGUI) {
            return;
        }
        this.debugGUI.setupMultiplierPanelDebug(this.multiplierPanels, this.collectionSymbol);
        this.debugGUI.setupJackpotCoinBowlDebug(this.jackpotBowl);
        this.debugGUI.setupWheelPrizePanelDebug(this.view, this.wheelPrizePanel, this.jackpotBowl, this.jackpotBowlLayerIndex);
        this.debugGUI.setupBonusTransitionDebug(this.bonusTransitionPanels);
        this.debugGUI.setupBonusSummaryScrollDebug(this.bonusSummaryScroll);
        this.debugGUI.setupSpinConfigEditor();
    }

    private setupComponents(): void {

        this.view.rootGameContent.addChild(this.background = new Background(this.services));
        this.view.rootGameContent.addChild(new BasicView(this.services, `reels-background`));
        this.view.rootGameContent.addChild(this.reels = new Reels(this.services));
        this.view.rootGameContent.addChild(new BasicView(this.services, `foreground`));
        this.view.rootGameContent.addChild(this.overlayMultiplierBadges = new OverlayMultiplierBadges(this.services));
        this.view.rootGameContent.addChild(this.winlines = new WinLines(this.services));
        this.view.rootGameContent.addChild(this.winlineCount = new WinlineCount(this.services));
        this.view.rootGameContent.addChild(this.multiplierPanels = new MultiplierPanels(this.services));
        this.view.rootGameContent.addChild(this.reelTopDisplay = new ReelTopDisplay(this.services));
        this.view.rootGameContent.addChild(this.freeSpinCounter = new FreeSpinCounter(this.services));
        this.view.rootGameContent.addChild(this.jackpotBowl = new JackpotBowl(this.services));
        this.view.rootGameContent.addChild(this.ksgLogo = new KSGLogo(this.services, this.services.layout));
        this.view.rootGameContent.addChild(this.precog = new Precog(this.services));
        this.view.rootGameContent.addChild(this.bonusSummaryScroll = new BonusSummaryScroll(this.services));
        this.view.rootGameContent.addChild(this.wheelPrizePanel = new WheelPrizePanel(this.services));
        this.view.rootGameContent.addChild(this.bonusTransitionPanels = new BonusTransitionPanels(this.services));
        this.view.rootGameContent.addChild(this.wheel = new Wheel(this.services));
        this.view.rootGameContent.addChild(this.collectionSymbol = new CollectionSymbol(this.services));
        this.view.rootGameContent.addChild(this.bigWin = new BigWin(this.services));

        this.resetList.push(this.background);
        this.resetList.push(this.reels);
        this.resetList.push(this.overlayMultiplierBadges);
        this.resetList.push(this.winlines);
        this.resetList.push(this.winlineCount);
        this.resetList.push(this.multiplierPanels);
        this.resetList.push(this.reelTopDisplay);
        this.resetList.push(this.freeSpinCounter);
        this.resetList.push(this.jackpotBowl);
        this.resetList.push(this.collectionSymbol);
        this.resetList.push(this.wheelPrizePanel);
        this.resetList.push(this.precog);
        this.resetList.push(this.wheel);
        this.resetList.push(this.bonusSummaryScroll);
        this.resetList.push(this.bonusTransitionPanels);
        this.resetList.push(this.bigWin);

        const reelsLayout = this.model.reels.setupLayout();
        const multiplierPanelsLayout = this.model.multiplierPanels.setupLayout(reelsLayout);

        this.model.wheelPrizePanel.setupLayout();

        this.reels.setup(reelsLayout);
        this.overlayMultiplierBadges.setup(reelsLayout);
        this.multiplierPanels.setup(multiplierPanelsLayout);

        this.winlines.signals.winLineComplete.add(() => this.winLineLoop(false), this);
        this.winlines.signals.scatterLineComplete.add((symbols, bonusComplete, value, loopIndex) => {if (loopIndex !== null) {this.scatterLineLoop(symbols, bonusComplete, value, ++loopIndex);}});

        this.reels.signals.bonusSymbolLand.add(this.bonusSymbolLand, this);
        this.reels.signals.reelStopSound.add(() => this.soundplayer.reelStop());
        this.reels.signals.reelStopPotCoin.add(this.reelStopPotCoin, this);
        this.reels.signals.topSymbolInteract.add(() => this.soundplayer.touchBuddhaLaugh());

        this.jackpotBowl.signals.startCoinPour.add(() => this.soundplayer.startJackpotBowlCoinPour());
        this.jackpotBowl.signals.coinCollision.add((coinNumber) => this.soundplayer.jackpotCoinCollision(coinNumber));

        this.wheel.signals.wheelIntro.add(() => this.soundplayer.wheelIntro());
        this.wheel.signals.wheelOutro.add(() => this.soundplayer.wheelOutro());
        this.wheel.signals.pointerCollision.add(() => this.soundplayer.wheelClick());

        this.freeSpinCounter.signals.totalCounterIncrement.add(() => this.soundplayer.freeSpinCounterIncrement());

        this.bigWin.signals.countupComplete.add(this.clearSkip, this);
        this.bigWin.signals.sounds.playCountup.add(() => this.soundplayer.bigWinCountUp());
        this.bigWin.signals.sounds.stopCountup.add(() => this.soundplayer.stopBigWinCountUp());
        this.bigWin.signals.sounds.playSting.add(() => this.soundplayer.bigWinSting());

        this.jackpotBowlLayerIndex = this.view.rootGameContent.getChildIndex(this.jackpotBowl);

        const cyclingSettings = this.services.resources.getSetting(`reelTopCycling`) as MessageCycleSettings;
        this.messageCycleController = new MessageCycleController(cyclingSettings);
        this.resetList.push(this.messageCycleController);

        this.messageCycleController.signals.cycle.add((index) => {
            switch (index) {
                case 0:
                    this.reelTopDisplay.transitionLogo();
                    break;
                case 1:
                    this.reelTopDisplay.transitionJackpots();
                    break;
            }
        });
    }

    private setupSkip(): void {

        this.signals.skipBigWin.add(() => {

            this.skipBigWin();
            this.skipWinRollUp();
        });

        if (this.services.rgsData.turboPlay) {

            this.signals.skipSpin.add(() => {

                this.precog.interrupt();
                this.soundplayer.skipSpin(this.model.inFreeSpinBonus);
                this.slamStop(this.model.reels.spinResult as ReelSetSymbols);
            });

            this.signals.skipWinRollUp.add(() => this.skipWinRollUp());
            this.signals.skipWheel.add(() => this.skipWheel());
            this.signals.skipFreeSpinCounter.add((total: number) => this.skipFreeSpinCounter(total));
            this.signals.skipJackpot.add(() => this.skipJackpot());
        }
    }

    private bonusSymbolLand(reelIndex: number): void {

        const playLandSound = this.model.playBonusLandSound[reelIndex];

        if (playLandSound) {
            this.soundplayer.bonusSymbolLand(reelIndex);
        }
    }

    private reelStopPotCoin(reelIndex: number): void {

        const reelResult = (this.model.reels.spinResult as ReelSetSymbols)[reelIndex];
        const symbolIndex = reelResult.indexOf(`B1`);

        if (symbolIndex !== -1) {

            const globalSymbolPos = this.reels.getSymbolGlobalPosition(reelIndex, symbolIndex);

            this.jackpotBowl.spawnNewCoin(globalSymbolPos);
            this.soundplayer.bonusSymbolCoin(this.model.preBonusWheel);
        }
    }

    private processFreeRounds(event: OpenGameEvent) {
        if (!event.body.data.FREE_ROUNDS || !event.body.data.FREE_ROUNDS.length) {
            return;
        }
        const freeRound = event.body.data.FREE_ROUNDS[0];

        if (freeRound.type !== `NUM_ROUNDS`) {
            console.error(`Unsupported FREE_ROUND type "${freeRound.type}"`);
            this.client.sendActivateFreeRoundRequest(freeRound.id, `DECLINE`);
            return;
        }

        this.gameDialogs.show({
            type: GameDialogType.FREE_ROUNDS_OFFER,
            resumingFreeRounds: freeRound.status === `ACTIVE`,
            availableBonusSpins: freeRound.data.numLeft,
            accept: () => {
                if (freeRound.status === `PENDING`) {
                    this.client.sendActivateFreeRoundRequest(freeRound.id, `ACCEPT`);
                }
                this.gameUI?.freeRoundsStarted(freeRound.conf.betAmount, freeRound.data.numLeft);
            }
        });

        this.model.setFreeRoundData(freeRound);
    }

    private openGame(event: OpenGameEvent): void {

        this.processFreeRounds(event);
        this.model.processOpenGameEvent(event);

        this.winlineCount.setWinLineCount(this.model.winLineCount);
        this.services.paytableContentFactory.setOpenGameData(this.services, event, this.model.winLineCount);

        const {data, lastEvent, lastRoundId} = event.body;
        const {betConfig, defaultReels, defaultReelStrips, paylinesRows, wedgesConfig} = data;

        this.flow.reset();
        this.setupGameUI(event, lastEvent?.data, lastRoundId);

        if (lastEvent) {
            this.messageCycleController.clearAndStopCycling();
            this.resumeGame(lastEvent.data);
        } else {
            this.model.state = GameState.IDLE;
            this.setReelTopDisplayJackpotValues(betConfig.defaultCoinSize, wedgesConfig);
            this.messageCycleController.idle();
        }

        const state = this.model.multiplierCountersState[this.model.retrieveCurrentCoinSize(event)];
        const multipliers = (this.model.inFreeSpinBonus) ? data.symbolMultipliers.FREE_SPIN : data.symbolMultipliers.INITIAL_SPIN;
        const activeMultipliers = this.model.calculateActiveMultipliers(multipliers, state);

        this.reels.setReelStrips(defaultReelStrips);
        this.reels.setCurrentReelSymbols(defaultReels, this.model.inFreeSpinBonus, activeMultipliers);

        this.winlines.setup(paylinesRows, this.model.reels.layout);
        this.setMultiplierPanelsOpenGameState(event);
        this.wheel.setup(this.model.wheel.orderedWedgeData);

        this.services.gameBridge.sendGameBridgeEvent({event: `setBalance`, payload: {balance: event.body.balance / this.services.currency.currencyConfig.minorDivisor}});
        this.services.gameBridge.sendGameBridgeEvent({event: `gameReady`});
        this.services.gameBridge.sendGameBridgeEvent({event: this.services.audio.soundEnabled ? `soundOn` : `soundOff`});

        this.loadingScreenHide.dispatch();

        this.flow.run();
    }

    private resumeGame(data: LastEventData): void {

        const {betData, gameplay, nextActions} = data;

        const inBonus = `finalFreeSpinsCounter` in gameplay;

        this.setReelTopDisplayJackpotValues(betData.coinSize, this.model.openGameData.wedgesConfig);

        if (inBonus) {
            this.reelTopDisplay.freeSpinsMode();
            this.addSetFreeSpinsToFlow(gameplay.finalFreeSpinsCounter);
        }

        if (nextActions[0] === GameRequest.WHEEL_SPIN) {
            this.model.state = (inBonus) ? GameState.FREE_SPIN_WHEEL_IDLE : GameState.WHEEL_IDLE;
            this.addWheelRevealToFlow(inBonus, nextActions, true);
        } else {
            this.model.state = GameState.FREE_SPIN;
        }

        this.soundplayer.bonusTheme();

        const freeRoundId = this.model.isFreeRound ? this.model.activeFreeRoundData.id : undefined;
        this.flow.addCallback(() => this.client.sendNextRequest(nextActions[0], betData.coinsPerBet, betData.coinSize, betData.playerBet, freeRoundId));
    }

    private initialSpin(event: InitialSpinEvent): void {

        const {freeRounds: freeRound} = event.body.data;
        if (freeRound) {
            this.model.setFreeRoundData(freeRound);
            if (freeRound.type === `NUM_ROUNDS`) {
                this.gameUI?.freeRoundsSpinsLeftChanged(freeRound.data.numLeft);
            }
        }

        this.services.gameBridge.sendGameBridgeEvent({event: `gameBusy`});

        const symbolMultipliers = this.model.openGameData.symbolMultipliers.INITIAL_SPIN;

        this.model.processSpinEvent(event, symbolMultipliers);

        const {elapsedTimeInMillis, netPosition} = event.body;
        const {accumulatedMoneyWon, balanceAfterBetData, betData, nextActions, roundId} = event.body.data;
        const {bonusSymbolsPositions, multipliers, reels} = event.body.data.gameplay;

        const goingToWheel = nextActions !== undefined;

        this.messageCycleController.interrupt();

        this.syncSessionElapsedTimeIfExists(elapsedTimeInMillis);
        this.gameUI?.updateTransactionId(roundId);
        this.updateBalance(balanceAfterBetData.balanceAfterBet, balanceAfterBetData.balanceAfterBetVersion);

        this.flow.reset();
        this.addDecrementAndHideMultiPanelsToFlow(multipliers, symbolMultipliers);
        this.addReelSpinToFlow(reels, false);
        this.addCollectSymbolsAndRevealMultiPanelsToFlow(multipliers, false);
        this.addUpdateWinningMultiPanelsToFlow(multipliers, symbolMultipliers);
        this.addBigWinToFlow(event, false);
        this.addSetWinDisplayConfigToFlow(false);
        this.addWinsToFlow(event, false);

        if (this.model.preBonusWheel) {
            this.flow.addCallback(() => this.soundplayer.bonusTheme());
            this.flow.addCallback(() => this.gameUI?.stopAutoPlay());
        }

        this.addScatterWinToFlow(false, bonusSymbolsPositions);
        this.addWheelRevealToFlow(false, nextActions);
        if (!goingToWheel) {
            this.flow.addCallback(() => this.messageCycleController.interruptionComplete());
        }
        if (!nextActions) {
            this.flow.add(new MinTimeBetweenWagersAction(this.services.rgsData.minTimeBetweenWagers));
        }
        this.addNextActionsHandlingToFlow(betData, accumulatedMoneyWon, netPosition, nextActions);
        this.addSessionCompleteGameBridgeToFlow(nextActions);
        this.flow.run();
    }

    private initialWheel(event: InitialWheelEvent): void {

        this.model.processWheelEvent(event);

        const {balance, balanceVersion, data, elapsedTimeInMillis, netPosition} = event.body;
        const {betData, accumulatedMoneyWon, nextActions, roundId, totalMoneyWon} = event.body.data;
        const {bonusTriggeringState} = data.gameplay;

        const returningToBaseGame = !nextActions;

        this.syncSessionElapsedTimeIfExists(elapsedTimeInMillis);
        this.gameUI?.updateTransactionId(roundId);

        if (bonusTriggeringState) {
            this.reels.setCurrentReelSymbols(bonusTriggeringState.reels, false);
        }

        this.flow.reset();
        this.addWheelSpinToFlow(data, false);
        this.addWheelPrizeCollectionToFlow(data, balance, balanceVersion, false, bonusTriggeringState);
        this.addBigWinToFlow(event, false);

        if (bonusTriggeringState) {

            this.addRestoreScatterWinToFlow(totalMoneyWon, bonusTriggeringState.bonusSymbolPositions, !nextActions);

            this.flow.addCallback(() => {
                this.soundplayer.stopBonusTheme();
            });
        }

        this.addNextActionsHandlingToFlow(betData, accumulatedMoneyWon, netPosition, nextActions);
        if (returningToBaseGame) {
            this.flow.addCallback(() => this.messageCycleController.interruptionComplete());
        } else {
            this.flow.addCallback(() => this.messageCycleController.clearAndStopCycling());
        }
        this.addSessionCompleteGameBridgeToFlow(nextActions);
        this.flow.run();
    }

    private freeSpin(event: FreeSpinEvent): void {

        const symbolMultipliers = this.model.openGameData.symbolMultipliers.FREE_SPIN;

        this.model.processSpinEvent(event, symbolMultipliers);
        this.model.state = GameState.FREE_SPIN;

        const {elapsedTimeInMillis, netPosition} = event.body;
        const {accumulatedMoneyWon, betData, nextActions, roundId} = event.body.data;
        const {bonusSymbolsPositions, bonusTriggeringState, finalFreeSpinsCounter, multipliers, reels} = event.body.data.gameplay;
        const {multiplierCountersMap} = event.body.data.finalPersistingState;
        const continuingFreeSpins = nextActions && nextActions[0] === GameRequest.FREE_SPIN;
        const goingToWheel = nextActions && nextActions[0] === GameRequest.WHEEL_SPIN;
        const returningToBaseGame = !continuingFreeSpins && !goingToWheel;

        this.syncSessionElapsedTimeIfExists(elapsedTimeInMillis);
        this.gameUI?.updateTransactionId(roundId);

        this.messageCycleController.interrupt();

        this.flow.reset();
        this.addSetFreeSpinsToFlow(finalFreeSpinsCounter);
        this.addDecrementAndHideMultiPanelsToFlow(multipliers, symbolMultipliers);
        this.addReelSpinToFlow(reels, true);
        this.addCollectSymbolsAndRevealMultiPanelsToFlow(multipliers, true);
        this.addUpdateWinningMultiPanelsToFlow(multipliers, symbolMultipliers);
        this.addSetWinDisplayConfigToFlow(true);
        this.addBigWinToFlow(event, true);
        this.addWinsToFlow(event, true);
        this.addScatterWinToFlow(true, bonusSymbolsPositions);
        this.addWheelRevealToFlow(true, nextActions);
        this.addExitBonusToFlow(event, multiplierCountersMap, bonusTriggeringState);

        if (bonusTriggeringState) {
            this.addRestoreScatterWinToFlow(accumulatedMoneyWon, bonusTriggeringState.bonusSymbolPositions, !nextActions);
        }
        if (continuingFreeSpins) {
            this.flow.addCallback(() => this.messageCycleController.interruptionComplete());
        } else if (returningToBaseGame) {
            this.flow.addCallback(() => this.messageCycleController.idle());
        }
        this.addNextActionsHandlingToFlow(betData, accumulatedMoneyWon, netPosition, nextActions);
        this.addSessionCompleteGameBridgeToFlow(nextActions);
        this.flow.run();
    }

    private freeSpinWheel(event: FreeSpinWheelEvent): void {

        this.model.processWheelEvent(event);

        const {balance, balanceVersion, data, elapsedTimeInMillis, netPosition} = event.body;
        const {accumulatedMoneyWon, betData, nextActions, roundId} = event.body.data;
        const {bonusTriggeringState, directTriggeringState} = event.body.data.gameplay;
        const {multiplierCountersMap} = event.body.data.finalPersistingState;

        const continuingFreeSpins = nextActions && nextActions[0] === GameRequest.FREE_SPIN;
        const goingToWheel = nextActions && nextActions[0] === GameRequest.WHEEL_SPIN;
        const returningToBaseGame = !continuingFreeSpins && !goingToWheel;

        this.syncSessionElapsedTimeIfExists(elapsedTimeInMillis);
        this.gameUI?.updateTransactionId(roundId);

        if (directTriggeringState) {
            this.reels.setCurrentReelSymbols(directTriggeringState.reels, true);
        }

        this.flow.reset();
        this.addWheelSpinToFlow(data, true);
        this.addWheelPrizeCollectionToFlow(data, balance, balanceVersion, true, bonusTriggeringState);
        this.addBigWinToFlow(event, true);
        this.addExitBonusToFlow(event, multiplierCountersMap, bonusTriggeringState);

        if (bonusTriggeringState) {
            this.addRestoreScatterWinToFlow(accumulatedMoneyWon, bonusTriggeringState.bonusSymbolPositions, false);
        }

        if (continuingFreeSpins) {
            this.flow.addCallback(() => this.messageCycleController.interruptionComplete());
        } else if (returningToBaseGame) {
            this.flow.addCallback(() => this.messageCycleController.idle());
        }
        this.addNextActionsHandlingToFlow(betData, accumulatedMoneyWon, netPosition, nextActions);
        this.addSessionCompleteGameBridgeToFlow(nextActions);
        this.flow.run();
    }

    private addDecrementAndHideMultiPanelsToFlow(multiplierData: Multipliers, symbolMultipliers: CoreSymbolNumberMap): void {

        this.flow.addCallback(() => {

            let id;

            for (const [symbolID, value] of Object.entries(multiplierData.initialCounters)) {

                id = symbolID as CoreSymbolID;

                this.multiplierPanels.setPanel(
                    getMultiplierPanelIndex(id),
                    id,
                    symbolMultipliers[id],
                    value > 0 ? value-1 : 0 //We can't use the final value here as it includes top ups from wins
                );
            }

            //Hidden panel data is not provided until the spin after the counter reaches zero
            for (const symbolID of multiplierData.removedSymbols) {
                this.multiplierPanels.hidePanel(getMultiplierPanelIndex(symbolID as CoreSymbolID));
            }
        });
    }

    private addReelSpinToFlow(spinResult: ReelSetSymbols, freeSpin: boolean): void {

        this.flow.addCallback(() => {

            const spinType = this.model.getSpinType(freeSpin);
            const spinConfig = this.model.reels.setSpinConfigForSpinType(spinType, this.model.anticipation);

            if (spinConfig.logSpinTime) {
                this.model.reels.startTime = Date.now();
            }

            this.reels.setSpinConfig(spinConfig);
            this.reels.spin(spinResult, this.model.inFreeSpinBonus, this.model.playLandAnim, this.model.activeMultipliers);

            if (spinConfig.instantSlam) {
                this.slamStop(spinResult);

            } else if (!freeSpin) {
                this.soundplayer.spin();
            }

            if (!spinConfig.instantSlam) {

                if (spinConfig.anticipationActive) {
                    this.precog.show(spinConfig.anticipationTime, this.model.shouldPrecogCoinShower);
                    this.soundplayer.precog(freeSpin);

                } else if (this.model.precogTease) {
                    this.soundplayer.precogTease(freeSpin);
                }
            }

            this.setSkip(this.signals.skipSpin);
        })
        .addSignalBlock(this.reels.signals.spinComplete)
        .addCallback(() => {

            if (this.model.reels.activeSpinConfig.logSpinTime) {
                console.log(`Spin Time(ms): `, Date.now() - this.model.reels.startTime);
            }

            this.clearSkip();
        });
    }

    private addCollectSymbolsAndRevealMultiPanelsToFlow(multipliers: Multipliers, freeSpin: boolean): void {

        const {symbolMultipliers} = this.model.openGameData;
        const symbolMultiplierSet = (freeSpin) ? symbolMultipliers.FREE_SPIN : symbolMultipliers.INITIAL_SPIN;

        const firstReelSymbolPositions = this.model.reels.layout.globalSymbolPositions[0];
        const showDelay = this.services.resources.getSetting(`multiplierPanel`).showDelay;

        let panelIndex: number;
        let multiplier, counter;

        for (const symbol of this.model.orderedSymbolsToCollect) {

            this.flow.addCallback(() => {
                this.collectionSymbol.configureForCollection(
                    symbol.id,
                    firstReelSymbolPositions[symbol.lowestRow],
                    1.0,
                    false
                );
            })
            .addDelay(1)
            .addCallback(() => {
                panelIndex = getMultiplierPanelIndex(symbol.id);
                this.collectionSymbol.collect(symbol.id, panelIndex, this.model.multiplierPanels.layout);
            })
            .addDelay(showDelay)
            .addCallback(() => {

                multiplier = symbolMultiplierSet[symbol.id];
                counter = multipliers.finalCounters[symbol.id];

                this.revealMultiplierPanel(panelIndex, symbol.id, multiplier, counter as number, false);
            })
            .addSignalBlock(this.multiplierPanels.signals.showSymbol)
            .addCallback(() => {

                this.collectionSymbol.reset();
                this.multiplierPanels.showPanelSymbol(panelIndex);
            });
        }

        this.flow.addCallback(() => this.soundplayer.resetMultiPanelIndex());
    }

    private addUpdateWinningMultiPanelsToFlow(multiplierData: Multipliers, symbolMultipliers: CoreSymbolNumberMap): void {

        this.flow.addCallback(() => {

            const collectedSymbolIDS = this.model.orderedSymbolsToCollect.map(item => item.id);
            let id;

            for (const [symbolID, value] of Object.entries(multiplierData.finalCounters)) {

                id = symbolID as CoreSymbolID;

                if (!collectedSymbolIDS.includes(id)) {

                    this.multiplierPanels.setPanel(
                        getMultiplierPanelIndex(id),
                        id,
                        symbolMultipliers[id],
                        value
                    );
                }
            }
        });
    }

    private addBigWinToFlow(event: InitialSpinEvent | FreeSpinEvent | InitialWheelEvent | FreeSpinWheelEvent,
        inFreeSpinBonus: boolean): void {

        if (this.model.bigWin) {

            const {balance, balanceVersion} = event.body;
            const {accumulatedMoneyWon, betData, totalMoneyWon} = event.body.data;

            this.flow.addCallback(() =>  {

                this.model.state = (inFreeSpinBonus) ? GameState.FREE_SPIN_SHOW_BIG_WIN : GameState.SHOW_BIG_WIN;
                this.reels.shake();

                if (inFreeSpinBonus) {
                    this.soundplayer.fadeOutBonusTheme();
                } else {
                    this.soundplayer.stopBonusTheme();
                }

                this.soundplayer.bonusBell();
            })
            .addSignalBlock(this.reels.signals.shakeComplete)
            .addCallback(() => {
                this.bigWin.start(betData.playerBet, totalMoneyWon);
                this.updateGameUIWinnings(accumulatedMoneyWon, balance, balanceVersion, false);

                this.setSkip(this.signals.skipBigWin);
            })
            .addSignalBlock(this.bigWin.signals.complete);

            if (inFreeSpinBonus) {

                this.flow.addCallback(() => {
                    this.soundplayer.fadeInBonusTheme();
                });
            }
        }
    }

    private addSetWinDisplayConfigToFlow(freeSpin: boolean): void {

        this.flow.addCallback(() => {

            const spinType = this.model.getSpinType(freeSpin);
            const winDisplayConfig = this.model.winlines.setWinDisplayConfigForSpinType(spinType);
            this.winlines.setWinDisplayConfig(winDisplayConfig);
        });
    }

    private addWinsToFlow(event: InitialSpinEvent | FreeSpinEvent, freeSpin: boolean): void {

        const {balance, balanceVersion} = event.body;
        const {accumulatedMoneyWon, betData,  gameplay, totalMoneyWon} = event.body.data;

        const isWin = gameplay.paylineWins.length > 0;

        if (isWin) {

            this.flow.addCallback(() => {

                this.model.state = (freeSpin) ? GameState.FREE_SPIN_SHOW_WINS : GameState.SHOW_WINS;
                this.updateGameUIWinnings(accumulatedMoneyWon, balance, balanceVersion, !this.model.bigWin);

                this.winLineLoop(true);

                if (totalMoneyWon <= betData.playerBet) {
                    this.soundplayer.consolation();
                } else {
                    this.soundplayer.winningSymbolSound(this.model.winningSymbolSoundID);
                }
            });

            if (this.gameUI && !this.model.bigWin) {
                this.flow.addSignalBlock(this.gameUI.signals.winRollupComplete);
            }
        }

        if (freeSpin) {
            this.flow.addDelay(this.services.resources.getSetting(`freeSpinPostWinsDelay`));
            if (isWin) {
                this.flow.addCallback(() => {
                    this.stopWinAnims();
                    this.soundplayer.stopWinningSymbolSound();
                });
            }
        }
    }

    private winLineLoop(firstLine: boolean): void {

        if (firstLine) {

            this.reels.startWinAnims(this.model.winningSymbolState);
            this.overlayMultiplierBadges.startWinAnims(this.model.multiplierBadgeWinData);
        }

        const winLineData = this.model.winlines.nextWinLineData();
        const timeBetweenWinsActive = (this.model.winlines.lineCount > 1)
        || this.model.state === GameState.IDLE
        || this.model.state === GameState.SHOW_WINS && !this.model.preBonusWheel;

        if (winLineData) {
            this.winlines.showWinLine(winLineData, timeBetweenWinsActive);
        } else {

            this.model.winlines.resetLineIndex();
            this.winLineLoop(false);
        }
    }

    private scatterLineLoop(symbols: {column: number, row: number}[], bonusComplete: boolean, value: number | null, loopIndex: number | null) {
        this.winlines.showScatterWin(symbols, bonusComplete, value, loopIndex);
    }

    private addScatterWinToFlow(freeSpin: boolean, bonusSymbols?: {column: number, row: number}[]): void {

        if (bonusSymbols) {

            this.flow.addCallback(() => {

                this.scatterLineLoop(bonusSymbols, false, null, null);
                this.reels.startScatterWinAnimationSequences(bonusSymbols);

                this.soundplayer.bonusSymbolWin();

                if (freeSpin) {
                    this.soundplayer.bonusBellRetrigger();
                } else {
                    this.soundplayer.bonusBell();
                }

            });

            if (freeSpin) {
                this.flow.addSignalBlock(this.soundplayer.signals.bonusBellRetriggerComplete);
            } else {
                this.flow.addSignalBlock(this.reels.signals.scatterWinComplete);
            }

            this.flow.addCallback(() => {
                this.winlines.hideAllWinBoxes();
            });
        }
    }

    private addRestoreScatterWinToFlow(moneyWon: number, bonusSymbols?: {column: number, row: number}[], bonusComplete?: boolean): void {

        if (bonusSymbols) {

            this.addSetWinDisplayConfigToFlow(false);

            this.flow.addCallback(() => {
                this.scatterLineLoop(bonusSymbols, bonusComplete || false, moneyWon, 0);
            })
            .addSignalBlock(this.winlines.signals.scatterLineComplete);
        }
    }

    private addWheelRevealToFlow(freeSpin: boolean, nextActions?: GameRequest[], resume=false): void {

        if (nextActions && nextActions[0] === GameRequest.WHEEL_SPIN) {

            this.flow.addCallback(() => {

                this.model.state = (freeSpin) ? GameState.FREE_SPIN_WHEEL_IDLE : GameState.WHEEL_IDLE;
                this.model.preBonusWheel = false;
                if (resume) {
                    this.wheel.resumeInto(freeSpin);
                } else {
                    this.wheel.reveal(freeSpin);
                }
            });
            if (!resume) {
                this.flow.addSignalBlock(this.wheel.signals.animateInComplete);
            }
            this.flow.addCallback(() => this.services.playerInactivity.start())
            .addCallback(() => {
                this.reels.stopWinAnims();
            })
            .addSignalBlock(this.wheel.signals.spinStart)
            .addCallback(() => this.services.playerInactivity.stop())
            .addCallback(() => {
                this.model.state = (freeSpin) ? GameState.FREE_SPIN_WHEEL_ACTIVE : GameState.WHEEL_ACTIVE;
            });
        }
    }

    private addWheelSpinToFlow(data: WheelData, freeSpin: boolean): void {

        const showWinningWedgeDelay = this.services.resources.getSetting(`wheel`).showWinningWedgeDelay;

        this.flow.addCallback(() => {

            this.wheel.spinToWinningWedge(this.model.wheel.winningWedgeIndex);
            this.setSkip(this.signals.skipWheel);
        })
        .addSignalBlock(this.wheel.signals.spinComplete)
        .addDelay(showWinningWedgeDelay)
        .addCallback(() => {
            this.wheel.showWedgeWin(data.gameplay.winningWedge);
            this.soundplayer.wheelPrize(data.gameplay.winningWedge);
        })
        .addSignalBlock(this.wheel.signals.showWedgeWinComplete)
        .addCallback(() => {

            this.clearSkip();

            this.model.state = (freeSpin) ? GameState.FREE_SPIN_WHEEL_COLLECT_PRIZE : GameState.WHEEL_COLLECT_PRIZE;

            if (data.nextActions) {
                this.reelTopDisplay.freeSpinsMode();
            }
        });
    }

    private addWheelPrizeCollectionToFlow(data: WheelData, balance: number, balanceVersion: number, freeSpin: boolean, bonusTriggeringState?: TriggeringState): void {

        const {accumulatedMoneyWon} = data;
        const {coinSize} = data.betData;
        const {cashWedgeAward, freeSpinsWedgeAward, finalFreeSpinsCounter, jackpotWedgeAward, multipliersWedgeAward, winningWedge} = data.gameplay;
        const {multiplierCountersMap} = data.finalPersistingState;

        const gui = this.gameUI as SlotGameUIView;

        this.flow.addCallback(() => {
            this.wheelPrizePanel.visible = true;
            this.wheel.slideOffScreen();
        })
        .addSignalBlock(this.wheel.signals.slideOffScreenComplete);

        if (cashWedgeAward) {

            this.flow.addCallback(() => {
                this.wheelPrizePanel.showCashPanel(cashWedgeAward.money);
            })
            .addSignalBlock(this.wheelPrizePanel.signals.startUICountUp)
            .addCallback(() => {
                this.updateGameUIWinnings(accumulatedMoneyWon, balance, balanceVersion);
            })
            .addSignalBlock(gui.signals.winRollupComplete)
            .addCallback(() => this.wheelPrizePanel.hidePanel())
            .addSignalBlock(this.wheelPrizePanel.signals.panelMoveStarted)
            .addCallback(() => {
                if (bonusTriggeringState) {
                    this.reels.startScatterWinAnimationLoops(bonusTriggeringState.bonusSymbolPositions);
                }
            })
            .addSignalBlock(this.wheelPrizePanel.signals.prizeSequenceComplete);

        } else if (multipliersWedgeAward) {

            const symbols = multipliersWedgeAward.multipliersSymbols;

            this.flow.addCallback(() => {
                this.wheelPrizePanel.showSymbolPanel(symbols, multipliersWedgeAward.money);
            })
            .addSignalBlock(this.wheelPrizePanel.signals.startSymbolCollection);

            this.addMultiplierWedgeWinToFlow(
                symbols,
                multiplierCountersMap,
                freeSpin ? false : data.isRoundFinished,
                coinSize,
                accumulatedMoneyWon,
                balance,
                balanceVersion,
                bonusTriggeringState
            );

        } else if (freeSpinsWedgeAward) {

            const {freeSpinsWon, money} = freeSpinsWedgeAward;
            const {defaultFreeSpinReels} = this.model.openGameData;

            this.flow.addCallback(() => {

                if (this.model.state === GameState.WHEEL_COLLECT_PRIZE) {
                    this.wheelPrizePanel.showFreeSpinsPanel(freeSpinsWon, money);
                } else {
                    this.wheelPrizePanel.showExtendFreeSpinsPanel(freeSpinsWon, money);
                }
                this.model.state = GameState.FREE_SPIN;
                this.reels.setCurrentReelSymbols(defaultFreeSpinReels, true);
            })
            .addSignalBlock(this.wheelPrizePanel.signals.startUICountUp)
            .addCallback(() => this.updateGameUIWinnings(accumulatedMoneyWon, balance, balanceVersion))
            .addSignalBlock(gui.signals.winRollupComplete)

            .addCallback(() => this.wheelPrizePanel.freeSpinsHold())
            .addSignalBlock(this.wheelPrizePanel.signals.prizeSequenceComplete)
            .addCallback(() => this.bonusTransitionPanels.playClosed())
            .addCallback(() => this.soundplayer.exitFreeSpinsTransition())
            .addSignalBlock(this.bonusTransitionPanels.signals.panelMoveInComplete)
            .addCallback(() => this.wheelPrizePanel.reset());

            if (finalFreeSpinsCounter) { // possible to get freespins wedge at freespins cap and not actually get more spins
                this.flow
                .addCallback(() => this.freeSpinCounter.countUpToTotalFreeSpins(finalFreeSpinsCounter.freeSpinsRemaining, finalFreeSpinsCounter.freeSpinsTotal))
                .addCallback(() => this.setSkip(this.signals.skipFreeSpinCounter, finalFreeSpinsCounter.freeSpinsTotal))
                .addSignalBlock(this.freeSpinCounter.signals.totalCountUpComplete)
                .addCallback(() => this.clearSkip());
            }

            this.flow
            .addCallback(() => this.bonusTransitionPanels.playOpen())
            .addSignalBlock(this.bonusTransitionPanels.signals.panelSequenceComplete);

            if (!freeSpin && this.model.multiplierCountersState[coinSize]) {
                this.addMultiplierPanelBonusTransitionToFlow(multiplierCountersMap, coinSize, true);
            }

        } else if (jackpotWedgeAward) {

            this.flow.addCallback(() => {

                this.wheelPrizePanel.showJackpotPanel(jackpotWedgeAward.money, winningWedge as JackpotWedgeID);

                this.soundplayer.fadeOutBonusTheme();
                this.soundplayer.wheelPrizeJackpotCelebration();

                this.wheelPrizePanel.jackpotBowlContainer.addChild(this.jackpotBowl);
                this.jackpotBowl.startWinLoop();

                this.setSkip(this.signals.skipJackpot);
            })
            .addSignalBlock(this.jackpotBowl.signals.countUpComplete)
            .addCallback(() => {

                this.clearSkip();
                this.jackpotBowl.endWinLoop();

                this.soundplayer.stopWheelPrizeJackpotCelebration();
                this.soundplayer.stopJackpotBowlCoinPour();
                this.soundplayer.fadeInBonusTheme();
            })
            .addSignalBlock(this.jackpotBowl.signals.winComplete)
            .addCallback(() => {

                this.view.rootGameContent.addChildAt(this.jackpotBowl, this.jackpotBowlLayerIndex);
                this.wheelPrizePanel.hidePanel();
            })
            .addSignalBlock(this.wheelPrizePanel.signals.panelMoveStarted)
            .addCallback(() => {
                if (bonusTriggeringState) {
                    this.reels.startScatterWinAnimationLoops(bonusTriggeringState.bonusSymbolPositions);
                }
            })
            .addSignalBlock(this.wheelPrizePanel.signals.prizeSequenceComplete)
            .addCallback(() => {
                this.wheelPrizePanel.reset();
            });
        }
    }

    private addMultiplierPanelBonusTransitionToFlow(multiplierCountersMap: MultiplierCountersMap, coinSize: number,
        toBonus: boolean): void {

        const {symbolMultipliers} = this.model.openGameData;
        const transitionTime = this.services.resources.getSetting(`multiplierPanel`).transitionTimePerPanel;

        let symbolID: CoreSymbolID;
        let panelIndex: number;

        const symbolIDs = Object.keys(this.model.multiplierCountersState[coinSize] || {});
        const updateOrder = [`M1`, `M2`, `M3`, `F4`, `F5`, `F6`];

        for (const id of updateOrder) {

            if (symbolIDs.includes(id)) {

                this.flow.addCallback(() => {

                    symbolID = id as CoreSymbolID;
                    panelIndex = getMultiplierPanelIndex(symbolID);

                    this.multiplierPanels.setPanel(
                        panelIndex,
                        symbolID,
                        (toBonus) ? symbolMultipliers.FREE_SPIN[symbolID] : symbolMultipliers.INITIAL_SPIN[symbolID],
                        multiplierCountersMap[coinSize][symbolID] as number,
                        true
                        );

                    this.soundplayer.transitionMultiPanel();
                })
                .addDelay(transitionTime);
            }
        }

        this.flow.addCallback(() => this.soundplayer.resetMultiPanelIndex());
    }

    private addSetFreeSpinsToFlow(finalFreeSpinsCounter?: FinalFreeSpinsCounter): void {

        if (finalFreeSpinsCounter) {

            this.flow.addCallback(() => {
                this.freeSpinCounter.visible = true;
                this.freeSpinCounter.setFreeSpins(finalFreeSpinsCounter.freeSpinsRemaining, finalFreeSpinsCounter.freeSpinsTotal);
            });
        }
    }

    private addMultiplierWedgeWinToFlow(symbols: CoreSymbolID[], multiplierCountersMap: MultiplierCountersMap,
        roundFinished: boolean, coinSize: number, accumulatedMoneyWon: number, balance: number, balanceVersion: number,
        bonusTriggeringState?: TriggeringState): void {

        const {symbolMultipliers} = this.model.openGameData;
        const symbolMultiplierSet = (roundFinished) ? symbolMultipliers.INITIAL_SPIN : symbolMultipliers.FREE_SPIN;

        let wheelPrizePanelDisplayPositions: PositionList;
        let symbolScale: number;

        if (symbols.length === 2) {
            wheelPrizePanelDisplayPositions = this.model.wheelPrizePanel.layout.globalSymbolDisplayPositions.twoSymbols;
            symbolScale = this.services.resources.getSetting(`wheelPrizePanel`).symbolPanel.symbols.finalScale.two;

        } else {
            wheelPrizePanelDisplayPositions = this.model.wheelPrizePanel.layout.globalSymbolDisplayPositions.sixSymbols;
            symbolScale = this.services.resources.getSetting(`wheelPrizePanel`).symbolPanel.symbols.finalScale.six;
        }

        const showDelay = this.services.resources.getSetting(`multiplierPanel`).showDelay;

        let panelIndex: number;
        let multiplier, counter;

        for (const [i, symbolID] of symbols.entries()) {

            this.flow.addCallback(() => {
                this.collectionSymbol.configureForCollection(
                    symbolID,
                    wheelPrizePanelDisplayPositions[i],
                    symbolScale,
                    true
                );
            })
            .addDelay(1)
            .addCallback(() => {
                panelIndex = getMultiplierPanelIndex(symbolID);
                this.multiplierPanels.resetPanel(panelIndex);

                this.wheelPrizePanel.hideSymbol(symbolID);
                this.collectionSymbol.signals.move.add(() => this.soundplayer.multiplierPanelSymbolMove());
                this.collectionSymbol.collect(symbolID, panelIndex, this.model.multiplierPanels.layout);
            })
            .addDelay(showDelay)
            .addCallback(() => {

                multiplier = symbolMultiplierSet[symbolID];
                counter = multiplierCountersMap[coinSize][symbolID];

                this.revealMultiplierPanel(panelIndex, symbolID, multiplier, counter as number, true);
            })
            .addSignalBlock(this.multiplierPanels.signals.showSymbol)
            .addCallback(() => {

                this.collectionSymbol.signals.move.detachAll();
                this.collectionSymbol.reset();
                this.multiplierPanels.showPanelSymbol(panelIndex);
            });
        }

        this.flow.addCallback(() =>  {

            this.updateGameUIWinnings(accumulatedMoneyWon, balance, balanceVersion);
            this.wheelPrizePanel.hideSymbolPanel();
            this.soundplayer.resetMultiPanelIndex();
        })
        .addSignalBlock(this.wheelPrizePanel.signals.panelMoveStarted)
        .addCallback(() => {
            if (bonusTriggeringState) {
                this.reels.startScatterWinAnimationLoops(bonusTriggeringState.bonusSymbolPositions);
            }
        })
        .addSignalBlock(this.wheelPrizePanel.signals.prizeSequenceComplete);
    }

    private addExitBonusToFlow(event: FreeSpinEvent | FreeSpinWheelEvent, multiplierCountersMap: MultiplierCountersMap, bonusTriggeringState?: TriggeringState): void {

        if (bonusTriggeringState) {

            const {balance, balanceVersion} = event.body;
            const {accumulatedMoneyWon, betData} = event.body.data;

            this.flow.addCallback(() => {

                this.updateBalance(balance, balanceVersion);
                this.model.state = GameState.FREE_SPIN_END;
                this.bonusSummaryScroll.show(accumulatedMoneyWon);
                this.soundplayer.scrollOpen();
            })
            .addSignalBlock(this.bonusSummaryScroll.signals.bonusSummaryComplete)
            .addCallback(() => {

                this.freeSpinCounter.reset();
                this.bonusTransitionPanels.playTransition();
                this.soundplayer.exitFreeSpinsTransition();
            })
            .addSignalBlock(this.bonusTransitionPanels.signals.panelMoveInComplete)
            .addCallback(() => {

                this.bonusSummaryScroll.reset();
                this.reelTopDisplay.baseGameMode();
                const activeMultipliers = this.model.calculateActiveMultipliers(
                    this.model.openGameData.symbolMultipliers.INITIAL_SPIN,
                    this.model.multiplierCountersState[betData.coinSize]
                );
                this.reels.setCurrentReelSymbols(bonusTriggeringState.reels, false, activeMultipliers);
                this.reels.startScatterWinAnimationLoops(bonusTriggeringState.bonusSymbolPositions);
                this.soundplayer.stopBonusTheme();
            })
            .addSignalBlock(this.bonusTransitionPanels.signals.panelSequenceComplete)
            .addCallback(() => {
                this.model.updateActiveMultipliers(this.model.openGameData.symbolMultipliers.INITIAL_SPIN);
            });

            this.addMultiplierPanelBonusTransitionToFlow(multiplierCountersMap, betData.coinSize, false);
        }
    }

    private addNextActionsHandlingToFlow(betData: BetData, accumulatedMoneyWon: number, netPosition?: NetPosition, nextActions?: GameRequest[]): void {

        this.flow.addCallback(() => {

            if (nextActions) {
                const freeRoundId = this.model.isFreeRound ? this.model.activeFreeRoundData.id : undefined;
                this.client.sendNextRequest(nextActions[0], betData.coinsPerBet, betData.coinSize, betData.playerBet, freeRoundId);

            } else {

                if (netPosition) {
                    this.gameUI?.updateNetProfit(netPosition.netProfit);
                }

                this.gameUI?.spinComplete(accumulatedMoneyWon);
                this.model.state = GameState.IDLE;
                this.reels.setTopSymbolInteraction(true);
            }
        });
    }

    private revealMultiplierPanel(panelIndex: number, symbolID: CoreSymbolID, multiplier: number, counter: number, wheelPrizeCollection=false): void {

        this.multiplierPanels.showPanel(panelIndex, symbolID, multiplier, counter, false, wheelPrizeCollection);
        this.soundplayer.revealMultiPanel();
    }

    private setMultiplierPanelsOpenGameState(openGameEvent: OpenGameEvent): void {

        const {body} = openGameEvent;
        const {betConfig, symbolMultipliers} = body.data;
        const betData = body.lastEvent?.data.betData;

        const currentCoinSize = betData !== undefined
            ? betData.coinSize
            : betConfig.defaultCoinSize;

        const state = this.model.multiplierCountersState[currentCoinSize];

        this.multiplierPanels.setPanels(
            (this.model.inFreeSpinBonus) ? symbolMultipliers.FREE_SPIN : symbolMultipliers.INITIAL_SPIN,
            state
        );
    }

    private setReelTopDisplayJackpotValues(selectedCoinSize: number, wedgesConfig: WedgesConfig): void {

        let jackpotValues = Object.values(wedgesConfig.jackpotWedgesCoinsAwardsMap);
        jackpotValues = jackpotValues.map(item => item * selectedCoinSize);

        this.reelTopDisplay.setJackpotvalues(jackpotValues[0], jackpotValues[1], jackpotValues[2]);
        this.wheel.setJackpotvalues(jackpotValues[0], jackpotValues[1], jackpotValues[2]);
    }

    private slamStop(spinResult: ReelSetSymbols): void {
        this.reels.slamStop(spinResult, this.model.inFreeSpinBonus, this.model.playLandAnim, this.model.activeMultipliers);
    }

    private skipWheel(): void {
        this.wheel.skipSpin();
    }

    private skipFreeSpinCounter(total: number): void {
        this.freeSpinCounter.skip(total);
    }

    private skipJackpot(): void {

        this.jackpotBowl.skipJackpot();
        this.soundplayer.stopWheelPrizeJackpotCelebration();
    }

    private skipBigWin(): void {

        this.bigWin.signals.skip.dispatch();
        this.soundplayer.stopBigWinCountUp();
    }

    private skipWinRollUp(): void {

        this.soundplayer.stopWinRollUp(this.model.bigWin, this.model.inFreeSpinBonus);
        this.soundplayer.stopWinningSymbolSound();
        this.gameUI?.skipWinRollup();
    }

    private stopWinAnims(): void {

        this.winlines.reset();
        this.reels.stopWinAnims();
        this.overlayMultiplierBadges.stopWinAnims();
    }

    /**
     *
     * @param dt Seconds
    */
    private tick(dt: number): void {

        if (this.model.state === GameState.IDLE) {
            this.multiplierPanels.tick(dt);

        } else if (this.model.state === GameState.WHEEL_ACTIVE || this.model.state === GameState.FREE_SPIN_WHEEL_ACTIVE) {
            this.wheel.tick(dt);
        }

        this.reelTopDisplay.tick(dt);
    }

    private resize(): void {

        const {layout} = this.services;

        this.winlineCount.resize(layout.orientation);
        this.freeSpinCounter.resize(layout.orientation);
        this.reelTopDisplay.resize(layout.orientation);
        this.ksgLogo.resize();
    }

    private spinError(event: SpinErrorEvent): void {
        this.gameUI?.spinCancelled();
        switch (event.body.data.code) {
            case `OUT_OF_MONEY`:
                showInsufficientFundsDialog(this.services, this.gameDialogs);
                break;
            case `FREE_ROUND_FINISHED`:
                showActivateFreeRoundFinishedDialog(this.services, this.gameDialogs, this.resetGame);
                break;
            case `GAMING_LIMITS_REACHED`:
                showGamingLimitsReachedErrorDialog(this.services, this.gameDialogs);
                break;
            default:
                console.warn(`Unexpected Spin Error`, event);
                showGenericCommsErrorDialog(this.services, this.gameDialogs, this.resetGame);
                break;
        }
    }

    private setupGameUI(event: OpenGameEvent, resumeData?: LastEventData, lastRoundId?: string): void {

        if (this.gameUI) {

            const {balance, balanceVersion, lastEvent, netPosition} = event.body;
            const {symbolMultipliers, paytable} = event.body.data;
            const {coinsPerBet, coinSizes, defaultCoinSize} = event.body.data.betConfig;

            let selectedCoin = defaultCoinSize;
            if (resumeData) {
                selectedCoin = resumeData.betData.coinSize;
            }
            if (event.body.data.FREE_ROUNDS && event.body.data.FREE_ROUNDS[0]) {
                const freeRound = event.body.data.FREE_ROUNDS[0];
                selectedCoin = freeRound.conf.defaultCoinSize;
            }

            const betConfig: UIBetConfig<"default"> = {
                coinSizes,
                coinsPerBet,
                defaultCoinSize,
                configName: `default`
            };

            this.gameUI.init({
                betConfigs: [betConfig],
                selectedBetConfigName: betConfig.configName,
                selectedCoin,
                balance,
                balanceVersion,
                netProfit: netPosition?.netProfit || 0,
                inBrokenGame: lastEvent !== undefined,
                brokenGameBet: lastEvent ? lastEvent.data.betData.playerBet : undefined,
                win: resumeData?.accumulatedMoneyWon,
                paytable: paytable,
                rtp: event.body.data.rtp !== undefined ? event.body.data.rtp * 100 : 95.002
            });

            const {wedgesConfig, wedgesSequence} = event.body.data;
            this.model.wheel.processWedgeData(wedgesConfig, wedgesSequence, selectedCoin);
            this.wheel.updateCashPrizes(this.model.wheel.orderedWedgeData);

            this.gameUI.signals.startSpin.add((spinData) => {

                this.stopWinAnims();
                this.multiplierPanels.clearIdleAnim();
                this.soundplayer.stopWinningSymbolSound();
                this.reels.setTopSymbolInteraction(false);

                this.model.state = GameState.SPIN;
                this.model.spinSpeedType = spinData.spinSpeed;
                this.model.spinModeType = spinData.spinMode;

                const freeRoundId = this.model.isFreeRound ? this.model.activeFreeRoundData.id : undefined;
                this.client.sendNextRequest(GameRequest.SPIN, spinData.coinsPerBet, spinData.selectedCoin, spinData.totalBet, freeRoundId);
            });

            this.gameUI.signals.betChanged.add((betData) => {

                this.multiplierPanels.reset();

                this.multiplierPanels.setPanels(
                    symbolMultipliers.INITIAL_SPIN,
                    this.model.multiplierCountersState[betData.selectedCoin]
                );

                this.setReelTopDisplayJackpotValues(betData.selectedCoin, this.model.openGameData.wedgesConfig);

                const {wedgesConfig, wedgesSequence} = event.body.data;
                this.model.wheel.processWedgeData(wedgesConfig, wedgesSequence, betData.selectedCoin);
                this.wheel.updateCashPrizes(this.model.wheel.orderedWedgeData);

                this.stopWinAnims();
                this.multiplierPanels.clearIdleAnim();
                this.soundplayer.stopWinningSymbolSound();
                const openGameData = this.model.openGameData;
                const activeMultipliers = this.model.calculateActiveMultipliers(symbolMultipliers.INITIAL_SPIN, this.model.multiplierCountersState[betData.selectedCoin]);
                this.reels.setCurrentReelSymbols(openGameData.defaultReels, false, activeMultipliers);
                this.reels.setTopSymbolInteraction(true);
            });

            this.gameUI.signals.openDeposit.add(() => this.services.gameBridge.sendGameBridgeEvent({event: `goToDeposit`}));
            this.gameUI.signals.playForReal.add(() => this.services.gameBridge.sendGameBridgeEvent({event: `playCashMode`}));
            this.gameUI.signals.openGameHistory.add(() => this.services.gameBridge.sendGameBridgeEvent({event: `openGameHistory`}));
            this.gameUI.signals.sounds.click.add(() => this.soundplayer.uiClick());
            this.gameUI.signals.sounds.spinClick.add(() => this.soundplayer.uiClick());
            this.gameUI.signals.winRollUpSkipEnable.add(() => this.setSkip(this.signals.skipWinRollUp));
            this.gameUI.signals.winRollupComplete.add(() => this.clearSkip());

            this.syncSessionElapsedTimeIfExists(event.body.elapsedTimeInMillis);

            if (lastRoundId) {
                this.gameUI?.updateTransactionId(lastRoundId);
            }
        }
    }

    private syncSessionElapsedTimeIfExists(elapsedTimeInMillis?: number): void {

        if (elapsedTimeInMillis !== undefined) {
            this.gameUI?.syncSessionElapsedTime(elapsedTimeInMillis);
        }
    }

    private updateGameUIWinnings(accumulatedMoneyWon: number, balance: number, balanceVersion: number, playSound=true): void {

        if (this.gameUI) {

            const duration = this.gameUI.setWin(accumulatedMoneyWon);
            this.updateBalance(balance, balanceVersion);

            if (playSound && duration > 0) {
                this.soundplayer.winRollUp(duration, this.model.inFreeSpinBonus);
            }
        }
    }

    private setSkip<SignalType extends Signal<(...args: Parameters<SignalType["dispatch"]>)=>void>>(signal: SignalType, ...args: Parameters<SignalType["dispatch"]>): void {
        this.gameUI?.signals.skip.once(() => signal.dispatch(...args));
        this.gameUI?.showSkipControls();
    }

    private clearSkip(): void {
        this.gameUI?.signals.skip.detachAll();
        this.gameUI?.hideSkipControls();
    }

    private periodicInspection(event: PeriodicInspectionEvent) {
        this.updateBalance(event.body.balance, event.body.balanceVersion);

        const {elapsedTimeInMillis, netPosition} = event.body;

        if (netPosition) {
            this.gameUI?.updateNetProfit(netPosition.netProfit);
        }

        this.syncSessionElapsedTimeIfExists(elapsedTimeInMillis);
    }

    private updateBalance(balance: number, balanceVersion: number) {
        if (this.gameUI?.updateBalance(balance, balanceVersion)) {
            this.services.gameBridge.sendGameBridgeEvent({event: `setBalance`, payload: {balance: balance / this.services.currency.currencyConfig.minorDivisor}});
        }
    }

    private addSessionCompleteGameBridgeToFlow(nextActions?: GameRequest[]) {
        if (!nextActions) {
            this.flow.addCallback(() => {
                this.services.gameBridge.sendGameBridgeEvent({event: `gameNotBusy`});
                this.services.gameBridge.sendGameBridgeEvent({event: `gameActivity`});

                if (this.model.activeFreeRoundsFinished) {
                    const winAmount = this.model.activeFreeRoundData.data.winAmount;
                    const formattedWinAmount = this.services.currency.formatCoins(winAmount, {group: true, addTrailingZeros: true, trimIntegers: true});
                    this.gameDialogs.show({
                        type: GameDialogType.FREE_ROUNDS_SUMMARY,
                        formattedWinAmount,
                        accept: () => {
                            this.model.freeRoundsComplete();
                            this.gameUI?.freeRoundsCompleted();
                        }
                    });

                }
            });

        }
    }
}